import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';

const RujulColumnFilter = ({
  columnOptions,
  columnHeadersMap,
  dataSets,
  onFilterApplied,
}) => {

  // Enhanced filter conditions with type awareness
  const getFilterConditions = (columnType) => {
    const commonConditions = [
      { value: '=', label: 'Is equal to' },
      { value: '!=', label: 'Is not equal to' },
    ];

    const textConditions = [
      { value: 'contains', label: 'Contains' },
      { value: 'notContains', label: 'Does not contain' },
      { value: 'startsWith', label: 'Starts with' },
      { value: 'endsWith', label: 'Ends with' },
    ];

    const numberConditions = [
      { value: '>', label: 'Is greater than' },
      { value: '>=', label: 'Is greater than or equal to' },
      { value: '<', label: 'Is less than' },
      { value: '<=', label: 'Is less than or equal to' },
    ];

    if (columnType === 'number') {
      return [...commonConditions, ...numberConditions];
    } else if (columnType === 'text') {
      return [...commonConditions, ...textConditions];
    }
    return commonConditions;
  };

  const [filterState, setFilterState] = useState({
    column: null,
    operator1: null,
    value1: '',
    operator2: null,
    value2: '',
    comparisonType: 'AND', // 'AND' or 'OR'
    columnType: null
  });

  const [availableOperators, setAvailableOperators] = useState([]);
  const [showSecondCondition, setShowSecondCondition] = useState(false);

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleColumnChange = (value) => {
    // Determine column type based on first data row
    let columnType = 'text'; // default
    if (dataSets && dataSets.length > 0) {
      const sampleValue = dataSets[0][value];
      if (typeof sampleValue === 'number') {
        columnType = 'number';
      } else if (!isNaN(sampleValue) && sampleValue.toString().trim() !== '') {
        columnType = 'number';
      }
    }

    const operators = getFilterConditions(columnType);
    setAvailableOperators(operators);

    setFilterState(prev => ({
      ...prev,
      column: value,
      operator1: null,
      value1: '',
      operator2: null,
      value2: '',
      columnType
    }));
  };

  const handleChange = (value, name) => {
    setFilterState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const compareValues = (itemValue, compareValue, operator, columnType) => {
    if (columnType === 'number') {
      const numValue = parseFloat(itemValue);
      const numCompare = parseFloat(compareValue);
      if (isNaN(numValue) || isNaN(numCompare)) return false;

      switch (operator) {
        case '=': return numValue === numCompare;
        case '!=': return numValue !== numCompare;
        case '>': return numValue > numCompare;
        case '>=': return numValue >= numCompare;
        case '<': return numValue < numCompare;
        case '<=': return numValue <= numCompare;
        default: return false;
      }
    } else {
      const strValue = String(itemValue).toLowerCase();
      const strCompare = String(compareValue).toLowerCase();

      switch (operator) {
        case '=': return strValue === strCompare;
        case '!=': return strValue !== strCompare;
        case 'contains': return strValue.includes(strCompare);
        case 'notContains': return !strValue.includes(strCompare);
        case 'startsWith': return strValue.startsWith(strCompare);
        case 'endsWith': return strValue.endsWith(strCompare);
        default: return false;
      }
    }
  };

useEffect(() => {
  // Only auto-filter if we have active filter conditions
  if (filterState.column && filterState.operator1 && filterState.value1 !== '') {
    handleGetData();
  }
}, [dataSets]);
  const handleGetData = () => {    
    const {
      column,
      operator1,
      value1,
      operator2,
      value2,
      comparisonType,
      columnType
    } = filterState;

    // Validate inputs
    if (!column || !operator1 || value1 === '' ||
      (showSecondCondition && (!operator2 || value2 === ''))) {
      console.warn("Please fill all required fields");
      return;
    }

    const filteredData = dataSets?.filter((item) => {
      const itemValue = item[column];

      // First condition evaluation
      const firstCondition = compareValues(itemValue, value1, operator1, columnType);

      // If we're only filtering with one condition
      if (!showSecondCondition) {
        return firstCondition;
      }

      // Second condition evaluation
      const secondCondition = compareValues(itemValue, value2, operator2, columnType);

      // Combine results based on logical operator
      return comparisonType === 'AND'
        ? firstCondition && secondCondition
        : firstCondition || secondCondition;
    });

    if (onFilterApplied) {
      onFilterApplied(filteredData || []);
    }
  };

  const handleReset = () => {
    setFilterState({
      column: null,
      operator1: null,
      value1: '',
      operator2: null,
      value2: '',
      comparisonType: 'AND',
      columnType: null
    });
    setAvailableOperators([]);
    setShowSecondCondition(false);
    if (onFilterApplied) {
      onFilterApplied(null);
    }
  };

  const toggleSecondCondition = () => {
    setShowSecondCondition(!showSecondCondition);
    if (!showSecondCondition) {
      setFilterState(prev => ({
        ...prev,
        operator2: null,
        value2: ''
      }));
    }
  };

  const isFilterValid = () => {
    const { column, operator1, value1, operator2, value2 } = filterState;

    if (!column || !operator1 || value1 === '') return false;
    if (showSecondCondition && (!operator2 || value2 === '')) return false;
    return true;
  };

  return (
    <div className="dropdown-menu custom-dropdown-width" aria-labelledby="excel-btn">
      <div className="container">
        <form onClick={stopPropagation}>
          {/* Column Selection */}
          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="mb-0">Column</Form.Label>
                <Select
                  options={columnOptions}
                  value={columnOptions?.find(opt => opt.value === filterState.column) || null}
                  placeholder="Select column"
                  onChange={(selected) => handleColumnChange(selected?.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label className="mb-0">Operator</Form.Label>
                <Select
                  options={availableOperators}
                  value={availableOperators?.find(opt => opt.value === filterState.operator1) || null}
                  placeholder="Select operator"
                  isDisabled={!filterState.column}
                  onChange={(selected) => handleChange(selected?.value, 'operator1')}
                />
              </Form.Group>
            </div>
               <div className="col-md-4">
              <Form.Group>
                <Form.Label className="mb-0">Value</Form.Label>
                <input
                  type={filterState.columnType === 'number' ? 'number' : 'text'}
                  className="form-control"
                  value={filterState.value1}
                  onChange={(e) => handleChange(e.target.value, 'value1')}
                  placeholder={`Enter ${filterState.columnType === 'number' ? 'number' : 'text'}`}
                />
              </Form.Group>
            </div>
          </div>

          {/* First Condition */}
          <div className="row mb-3">
            
         
          </div>

          {/* Toggle for second condition */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Check
                type="switch"
                id="custom-switch"
                label="Add second condition"
                checked={showSecondCondition}
                onChange={toggleSecondCondition}
              />
            </div> 
          </div>

          {/* Second Condition (conditional) */}
          {showSecondCondition && (
            <>
              <div className="row mb-3">
                 <div className="col-md-4">
                  <Form.Group>
                    <Form.Label className="mb-0">Combine With</Form.Label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn ${filterState.comparisonType === 'AND' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleChange('AND', 'comparisonType')}
                      >
                        AND
                      </button>
                      <button
                        type="button"
                        className={`btn ${filterState.comparisonType === 'OR' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleChange('OR', 'comparisonType')}
                      >
                        OR
                      </button>
                    </div>
                  </Form.Group>
                </div>
               <div className="col-md-4">
                  <Form.Group>
                    <Form.Label className="mb-0">Second Operator</Form.Label>
                    <Select
                      options={availableOperators}
                      value={availableOperators?.find(opt => opt.value === filterState.operator2) || null}
                      placeholder="Select operator"
                      isDisabled={!filterState.column}
                      onChange={(selected) => handleChange(selected?.value, 'operator2')}
                    />
                  </Form.Group>
                </div>
                 
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label className="mb-0">Second Value</Form.Label>
                    <input
                      type={filterState.columnType === 'number' ? 'number' : 'text'}
                      className="form-control"
                      value={filterState.value2}
                      onChange={(e) => handleChange(e.target.value, 'value2')}
                      placeholder={`Enter ${filterState.columnType === 'number' ? 'number' : 'text'}`}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row mb-3">
              
              </div>
            </>
          )}
        </form>

        {/* Action Buttons */}
        <div className="row mt-3">
          <div className="text-end gap-2">
            <button
              className="custom-btn me-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleReset();
              }}
              type="button"
            >
              Reset
            </button>
            <button
              className="custom-btn"
              onClick={handleGetData}
              type="button"
              disabled={!isFilterValid()}
            >
              Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RujulColumnFilter;